import {
  Component,
  Input,
  OnInit,
  ViewChild,
  TemplateRef,
  Output,
  EventEmitter,
} from '@angular/core';
import {
  UntypedFormGroup,
  UntypedFormBuilder,
  Validators,
  AbstractControl,
} from '@angular/forms';
import {
  LearmoModalRef,
  LearmoModalService,
} from 'src/app/shared/services/learmo-modal.service'; // Adjust path if needed
import { ApiService } from 'src/app/shared/services/lsg.api.service';
import { PageItem } from 'src/app/models/portal';
import { ToastrService } from 'ngx-toastr';
import { lastValueFrom } from 'rxjs';
import { PromptingService } from 'src/app/shared/services/prompting.service';
import { SharedPortalViewService } from 'src/app/components/user-dashboard/portal/view/shared-portal-view.service';

@Component({
  selector: 'app-page-settings-modal',
  templateUrl: './page-settings-modal.component.html',
  styleUrls: ['./page-settings-modal.component.css'],
})
export class PageSettingsModalComponent implements OnInit {
  @Input() page: PageItem;
  @Output() onSettingsUpdated: EventEmitter<PageItem> =
    new EventEmitter<PageItem>();

  settingsForm: UntypedFormGroup;
  modalRef: LearmoModalRef;
  processing = false;
  slugError: string = '';
  initialFormValue: any;

  @ViewChild('modalContent') modalContent: TemplateRef<any>;
  @ViewChild('modalFooter') modalFooter: TemplateRef<any>;

  constructor(
    private modalService: LearmoModalService,
    private fb: UntypedFormBuilder,
    private api: ApiService,
    private toastr: ToastrService,
    private prompting: PromptingService,
    public sharedPortalViewService: SharedPortalViewService,
  ) {}

  ngOnInit(): void {
    this.buildForm();
  }

  buildForm() {
    this.settingsForm = this.fb.group({
      title: [this.page?.title || '', [Validators.required]],
      slug: [
        this.page?.slug || '',
        [Validators.required, Validators.pattern(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)],
      ],
      showHeader: [this.page?.showHeader !== false], // Default to true if undefined
      showFooter: [this.page?.showFooter !== false],
    });

    this.initialFormValue = this.settingsForm.value;

    // Auto-normalize slug on change
    this.settingsForm.get('slug').valueChanges.subscribe((value) => {
      this.slugError = '';
      if (value) {
        const normalized = this.normalizeSlug(value);
        if (value !== normalized) {
          this.settingsForm
            .get('slug')
            .setValue(normalized, { emitEvent: false });
        }
      }
    });
  }

  async checkSlugAvailability() {
    const slug = this.settingsForm.get('slug')?.value;
    if (!slug || slug === this.page?.slug) return;

    if (this.settingsForm.get('slug')?.invalid) return;

    try {
      const checkRes: any = await lastValueFrom(this.api.checkSlugUnique(slug));
      if (checkRes.exists) {
        this.slugError = 'This URL slug has been taken.';
        this.settingsForm.get('slug').setErrors({ notUnique: true });
      } else {
        this.slugError = '';
        const errors = this.settingsForm.get('slug').errors;
        if (errors) {
          delete errors['notUnique'];
          if (Object.keys(errors).length === 0) {
            this.settingsForm.get('slug').setErrors(null);
          } else {
            this.settingsForm.get('slug').setErrors(errors);
          }
        }
      }
    } catch (e) {
      console.error('Failed to check slug', e);
    }
  }

  normalizeSlug(value: string): string {
    return value
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }

  async open() {
    this.buildForm(); // Re-build to reset state
    this.modalRef = this.modalService.open(this.modalContent, {
      title: 'Page Settings',
      footer: this.modalFooter,
      size: 'md',
    });
  }

  close() {
    if (this.settingsForm.dirty) {
      this.prompting
        .confirm({
          title: 'Unsaved Changes',
          html: 'You have unsaved changes. Are you sure you want to discard them?',
          confirmButtonText: 'Discard',
          cancelButtonText: 'Keep Editing',
          icon: 'warning',
        })
        .then((res) => {
          if (res.isConfirmed) {
            this.modalRef.dismiss();
          }
        });
    } else {
      this.modalRef.dismiss();
    }
  }

  async save() {
    this.settingsForm.markAllAsTouched();
    if (this.settingsForm.invalid) return;

    this.processing = true;
    const formValue = this.settingsForm.value;

    try {
      // Check slug uniqueness if changed
      // Check slug uniqueness if changed
      if (formValue.slug !== this.page.slug) {
        const checkRes: any = await lastValueFrom(
          this.api.checkSlugUnique(formValue.slug),
        );
        if (checkRes.exists) {
          this.toastr.error(
            'This URL slug is already taken. Please choose another.',
          );
          this.slugError = 'This URL slug has been taken.';
          this.settingsForm.get('slug').setErrors({ notUnique: true });
          this.processing = false;
          return;
        }
      }

      // Save settings
      const updateRes: any = await lastValueFrom(
        this.api.updatePageSettings(this.page.id, formValue),
      );

      this.toastr.success('Page settings updated successfully');

      // The backend returns the page object directly
      const updatedPage = updateRes.page || updateRes; // Handle both wrapped and unwrapped just in case

      this.onSettingsUpdated.emit(
        updatedPage || { ...this.page, ...formValue },
      );
      this.modalRef.close();
    } catch (err: any) {
      this.toastr.error(err?.message || 'Failed to update settings');
    } finally {
      this.processing = false;
    }
  }
}
